import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone } from "lucide-react";

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_9ddd47f9-cbc8-403c-b0f1-77e6c6e430be/artifacts/jabab0k2_Asset%205%404x.png";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Tours", path: "/tours" },
  { label: "Flights", path: "/flights" },
  { label: "Cruises", path: "/cruises" },
  { label: "Antarctica", path: "/expeditions/antarctica" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-[180ms] ${
        scrolled ? "shadow-nav" : ""
      }`}
      data-testid="navbar"
    >
      {/* Skip to main content — keyboard accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-4 focus:z-[9999] focus:bg-accent focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-medium"
      >
        Skip to main content
      </a>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo">
            <img
              src={LOGO_DARK}
              alt="Tripsyfy"
              className="h-8 sm:h-9 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-[120ms] ${
                  location.pathname === link.path
                    ? "text-accent"
                    : "text-primary/70 hover:text-primary hover:bg-secondary/60"
                }`}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <Button variant="ghost" size="sm" className="gap-1.5 text-primary/70 hover:text-primary" asChild data-testid="navbar-call-btn">
              <a href="tel:+19726661122">
                <Phone className="h-3.5 w-3.5" />
                +1 972 666 1122
              </a>
            </Button>
            <Link to="/contact">
              <Button size="sm" className="gap-2 bg-accent text-white hover:bg-accent/90" data-testid="navbar-quote-btn">
                Get a Quote
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 rounded-lg text-primary/70 hover:text-primary hover:bg-secondary/60 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 top-16 bg-white z-40 overflow-y-auto animate-fade-in-up"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-6 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center py-3 px-4 rounded-xl text-base font-medium transition-colors duration-[120ms] ${
                  location.pathname === link.path
                    ? "text-accent bg-surface-soft"
                    : "text-primary/80 hover:text-primary hover:bg-secondary/60"
                }`}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-6 mt-2 border-t border-border space-y-3">
              <Button variant="outline" className="w-full gap-2" size="lg" asChild>
                <a href="tel:+19726661122">
                  <Phone className="h-4 w-4" />
                  +1 972 666 1122
                </a>
              </Button>
              <Link to="/contact">
                <Button className="w-full bg-accent text-white hover:bg-accent/90" size="lg" data-testid="mobile-quote-btn">
                  Get a Free Quote
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};
